import { createContext, ReactNode, useEffect, useRef, useMemo } from 'react';
import { useDecisionVoucher, UseDecisionVoucherReturn } from '../hooks/useDecisionVoucher';

// Interface qui étend les propriétés du hook useDecisionVoucher
// interface DecisionVoucherContextType extends UseDecisionVoucherReturn {}

// Le contexte utilise directement le type de retour du hook
export const DecisionVoucherContext = createContext<UseDecisionVoucherReturn | null>(null);

interface DecisionVoucherProviderProps {
  savId: number;
  children: ReactNode;
}

/**
 * Provider pour le contexte DecisionVoucher
 */
export function DecisionVoucherProvider({ savId, children }: DecisionVoucherProviderProps) {
  // Utilisation du hook personnalisé
  const decisionVoucherState = useDecisionVoucher(savId);
  const initialLoadDone = useRef(false);

  // Chargement initial du bon, une seule fois
  useEffect(() => {
    if (!initialLoadDone.current) {
      // Fonction interne pour éviter les dépendances circulaires
      const initializeData = async () => {
        await decisionVoucherState.loadVoucher();
        await decisionVoucherState.loadHistory();
        initialLoadDone.current = true;
      };

      initializeData();
    }
    // Pas de dépendances - exécuté une seule fois lors du montage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mémoriser la valeur du contexte pour éviter les rendus inutiles
  const memoizedValue = useMemo(() => decisionVoucherState, [
    decisionVoucherState.voucher,
    decisionVoucherState.isLoading,
    decisionVoucherState.error,
    decisionVoucherState.isDirty,
    decisionVoucherState.isSaving,
    decisionVoucherState.saveError,
    decisionVoucherState.voucherHistory
  ]);

  return (
    <DecisionVoucherContext.Provider value={memoizedValue}>
      {children}
    </DecisionVoucherContext.Provider>
  );
}
